/**
 * Redaction helpers for error monitoring.
 *
 * Nothing sensitive should ever reach the monitoring store: no email
 * addresses, no money amounts, no free-text the user typed (transaction
 * descriptions, money fears, notes), and no AI prompts.
 */

const EMAIL_RE = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
const MONEY_RE = /(?:[£$€]\s?\d[\d,]*(?:\.\d+)?)|(?:\b\d[\d,]*(?:\.\d{2})\b)/g;
const LONG_NUMBER_RE = /\b\d{4,}\b/g;
const UUID_RE = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;
const JWT_RE = /\beyJ[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}\b/g;
const TOKEN_RE = /\b(sk|pk|rk)_(live|test)_[A-Za-z0-9]+\b/g;

export const MAX_MESSAGE_LENGTH = 500;
export const MAX_STACK_LENGTH = 2000;

/** Strip anything that could identify a person or reveal financial data. */
export function scrubText(input: unknown, maxLength = MAX_MESSAGE_LENGTH): string {
  if (input == null) return '';
  const raw = typeof input === 'string' ? input : String(input);
  const cleaned = raw
    .replace(JWT_RE, '[redacted-token]')
    .replace(TOKEN_RE, '[redacted-token]')
    .replace(EMAIL_RE, '[redacted-email]')
    .replace(UUID_RE, '[redacted-id]')
    .replace(MONEY_RE, '[redacted-amount]')
    .replace(LONG_NUMBER_RE, '[redacted-number]')
    .trim();
  return cleaned.length > maxLength ? `${cleaned.slice(0, maxLength)}…` : cleaned;
}

/** Remove query strings and hashes — they can carry tokens or ids. */
export function scrubRoute(route: string | undefined | null): string {
  if (!route) return '';
  const path = route.split('?')[0].split('#')[0];
  return scrubText(path, 200);
}

/**
 * Only structural values survive into `context`: booleans, short scrubbed
 * strings, and numbers whose key is a known non-financial counter.
 * Objects, arrays and any other number are dropped entirely.
 */
const SAFE_NUMBER_KEYS = new Set([
  'status',
  'statusCode',
  'attempt',
  'count',
  'durationMs',
  'retries',
  'itemCount',
]);

export function scrubContext(context: Record<string, unknown> | undefined): Record<string, unknown> {
  if (!context) return {};
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(context)) {
    if (typeof value === 'boolean') {
      out[key] = value;
    } else if (typeof value === 'number' && Number.isFinite(value) && SAFE_NUMBER_KEYS.has(key)) {
      out[key] = value;
    } else if (typeof value === 'string') {
      out[key] = scrubText(value, 120);
    }
  }
  return out;
}

